<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { CONTAINER_COLOR_VARIANTS } from '../variants';

	export const scrollVariants = tv({
		// The hidden axis is set per direction: an `overflow-hidden` shorthand in the base would
		// win over the axis utility depending on Tailwind's rule order and stop the box scrolling.
		base: 'ui-scroll size-full',
		variants: {
			variant: CONTAINER_COLOR_VARIANTS,
			direction: {
				Horizontal: 'overflow-x-scroll overflow-y-hidden',
				Vertical: 'overflow-x-hidden overflow-y-scroll',
				Both: 'overflow-scroll'
			}
		},
		defaultVariants: { variant: 'Surface', direction: 'Vertical' }
	});

	export interface ScrollProps
		extends Omit<HTMLAttributes<HTMLElement>, 'class'>, VariantProps<typeof scrollVariants> {
		as?: string;
		children?: Snippet;
		class?: ClassValue;
		hideTrack?: boolean;
		ref?: HTMLElement | null;
		size?: '0' | '300' | '400';
		visibility?: 'Always' | 'Hover';
	}
</script>

<script lang="ts">
	let {
		as = 'div',
		children,
		class: className,
		direction = 'Vertical',
		hideTrack = false,
		ref = $bindable(null),
		size = '400',
		variant,
		visibility = 'Always',
		...rest
	}: ScrollProps = $props();

	$effect(() => {
		if (!ref) return;
		if (size === '0') {
			ref.dataset.xScrollbarWidth = '0';
			ref.dataset.yScrollbarWidth = '0';
			return;
		}
		const xScrollbarWidth = ref.offsetHeight - ref.clientHeight;
		const yScrollbarWidth = ref.offsetWidth - ref.clientWidth;
		ref.dataset.xScrollbarWidth = `${xScrollbarWidth}`;
		ref.dataset.yScrollbarWidth = `${yScrollbarWidth}`;
	});
</script>

<svelte:element
	this={as}
	bind:this={ref}
	class={[scrollVariants({ direction, variant }), className]}
	data-direction={direction}
	data-hide-track={hideTrack ? '' : undefined}
	data-size={size}
	data-visibility={visibility}
	{...rest}
>
	{@render children?.()}
</svelte:element>

<style>
	.ui-scroll {
		--scroll-track: var(--v-container-hover);
		--scroll-thumb: var(--v-container-line);
	}
	.ui-scroll[data-hide-track] {
		--scroll-track: transparent;
	}
	.ui-scroll::-webkit-scrollbar-thumb {
		background-color: transparent;
		border-radius: var(--radius-pill);
		min-height: 2.1875rem;
	}
	.ui-scroll::-webkit-scrollbar-track {
		background-color: transparent;
		border-radius: var(--radius-pill);
	}
	.ui-scroll::-webkit-scrollbar-corner {
		background-color: transparent;
	}
	.ui-scroll[data-visibility='Always']::-webkit-scrollbar-thumb,
	.ui-scroll[data-visibility='Hover']:hover::-webkit-scrollbar-thumb {
		background-color: var(--scroll-thumb);
	}
	.ui-scroll[data-visibility='Always']::-webkit-scrollbar-track,
	.ui-scroll[data-visibility='Hover']:hover::-webkit-scrollbar-track {
		background-color: var(--scroll-track);
	}
	.ui-scroll[data-size='0']::-webkit-scrollbar {
		display: none;
	}
	.ui-scroll[data-size='300']::-webkit-scrollbar {
		width: 0.5rem;
		height: 0.5rem;
	}
	.ui-scroll[data-size='300']::-webkit-scrollbar-thumb,
	.ui-scroll[data-size='300']::-webkit-scrollbar-track {
		border: 0.125rem solid transparent;
		background-clip: padding-box;
	}
	.ui-scroll[data-size='400']::-webkit-scrollbar {
		width: 1rem;
		height: 1rem;
	}
	.ui-scroll[data-size='400']::-webkit-scrollbar-thumb,
	.ui-scroll[data-size='400']::-webkit-scrollbar-track {
		border: 0.25rem solid transparent;
		background-clip: padding-box;
	}
	@supports (-moz-appearance: none) {
		.ui-scroll {
			scrollbar-color: transparent transparent;
		}
		.ui-scroll[data-visibility='Always'],
		.ui-scroll[data-visibility='Hover']:hover {
			scrollbar-color: var(--scroll-thumb) var(--scroll-track);
		}
		.ui-scroll[data-size='0'] {
			scrollbar-width: none;
		}
		.ui-scroll[data-size='300'] {
			scrollbar-width: thin;
		}
		.ui-scroll[data-size='400'] {
			scrollbar-width: auto;
		}
	}
	/* Overlay scrollbars report width 0; pad so content keeps the same gutter as classic scrollbars. */
	.ui-scroll[data-direction='Horizontal'][data-size='300'][data-x-scrollbar-width='0'],
	.ui-scroll[data-direction='Both'][data-size='300'][data-x-scrollbar-width='0'] {
		padding-bottom: 0.5rem;
	}
	.ui-scroll[data-direction='Horizontal'][data-size='400'][data-x-scrollbar-width='0'],
	.ui-scroll[data-direction='Both'][data-size='400'][data-x-scrollbar-width='0'] {
		padding-bottom: 1rem;
	}
	.ui-scroll[data-direction='Vertical'][data-size='300'][data-y-scrollbar-width='0'],
	.ui-scroll[data-direction='Both'][data-size='300'][data-y-scrollbar-width='0'] {
		padding-right: 0.5rem;
	}
	.ui-scroll[data-direction='Vertical'][data-size='400'][data-y-scrollbar-width='0'],
	.ui-scroll[data-direction='Both'][data-size='400'][data-y-scrollbar-width='0'] {
		padding-right: 1rem;
	}
</style>
